import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { api } from "./api.js";
import { authReducer, persistSession, type AuthState } from "./auth.slice.js";
import { uploadsReducer, type UploadsState } from "./uploads.slice.js";

export interface RootState {
  auth: AuthState;
  uploads: UploadsState;
  /**
   * The RTK Query cache slice. Opaque on purpose: it is read only through the
   * generated hooks, which carry their own types, so nothing in this app has a
   * reason to reach into it.
   */
  api: unknown;
}

export const store = configureStore({
  reducer: { auth: authReducer, uploads: uploadsReducer, [api.reducerPath]: api.reducer },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // `meta.baseQueryMeta` holds the raw fetch Request/Response, and the
        // publish/upload mutations carry a `File` that RTK Query keeps in
        // `originalArgs`; none of them can be JSON, and all of them are
        // deliberate — without these the dev console fills with warnings.
        ignoredActionPaths: [
          "meta.baseQueryMeta",
          "meta.arg.originalArgs.bundle",
          "meta.arg.originalArgs.binary",
        ],
        ignoredPaths: [`${api.reducerPath}.mutations`],
      },
    }).concat(api.middleware),
});

export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

let lastAuth = store.getState().auth;
store.subscribe(() => {
  const nextAuth = store.getState().auth;
  if (nextAuth === lastAuth) return;
  const wasSignedIn = lastAuth.accessToken !== null;
  lastAuth = nextAuth;
  persistSession(nextAuth);
  // Sign-out must also drop every cached response: the next operator on this
  // browser would otherwise see the previous one's releases, users and audit.
  if (wasSignedIn && nextAuth.accessToken === null) {
    store.dispatch(api.util.resetApiState());
  }
});
